<template>
  <div class="game mt-3">
    <div class="container" v-if="gameConfig.players && gameConfig.players.length === 1">
      <div class="row">
        <div class="col-12 col-md-10 col-lg-8 col-xl-6 mx-auto text-center">
          <div class="card mb-4">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <h2 class="font-weight-light m-0">{{ gameConfig.min }}+{{ gameConfig.inc }} &middot; {{ gameConfig.mode | cap }} &middot; <span class="mr-1" :class="types[gameConfig.type].icon" :style="`--fa-primary-color: ${types[gameConfig.type].colorPrimary}; --fa-secondary-color: ${types[gameConfig.type].colorSecondary};`"></span>{{ gameConfig.type | cap }}</h2>
                  <div class="row">
                    <div class="col-9 mx-auto">
                      <hr>
                      <template v-if="isRoomCreator()">
                        <p class="lead mt-2 m-0" v-if="gameConfig.lobby">Your game is in the lobby, please wait on this page for another player to join.</p>
                        <hr v-if="gameConfig.lobby" />
                        <p class="lead mt-2 m-0" v-if="gameConfig.lobby">Invite a friend if you don't want to wait.</p>
                        <p class="lead mt-2 m-0" v-if="!gameConfig.lobby">Invite a friend by sharing the page link.</p>
                        <a href="#" @click.prevent="copyText(link)" class="btn btn-outline-dark mt-3 mb-2">Copy page link</a>
                        <p class="lead mt-2 m-0" v-if="showCopied">Link copied to clipboard.</p>
                      </template>
                      <template v-if="!isRoomCreator()">
                        <strong>{{ gameConfig.players[0].username }}</strong> is waiting for a player here.
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!isRoomCreator()">
            <div v-if="gameConfig.mode === 'casual' && !showSignupBox">
              <a href="#" @click.prevent="joinTheGame" class="btn btn-lg btn-dark">Play as guest</a>
              <p class="lead m-0">or</p>
              <a @click.prevent="joinAsAUser" href="#" class="btn btn-lg btn-link">Quick signup &amp; play</a>
            </div>
            <div v-if="getCurrentUser && gameConfig.mode === 'rated'">
              <template v-if="!getCurrentUser.isAnonymous">
                <a href="#" @click.prevent="joinTheGame" class="btn btn-lg btn-dark">Join the game</a>
              </template>
              <template v-if="getCurrentUser.isAnonymous">
                <template v-if="!showSignupBox">
                  <p class="lead">Guests can play <strong class="font-weight-bold">casual</strong> games.</p>
                  <p class="lead"><a @click.prevent="joinAsAUser" href="#">Join as a user</a> to play this <strong class="font-weight-bold">rated</strong> game.</p>
                  <a @click.prevent="joinAsAUser" href="#" class="btn btn-lg btn-outline-dark mb-3">Join as a user</a>
                </template>
              </template>
            </div>
            <div class="row" v-if="showSignupBox">
              <div class="col-12">
                <h2 class="text-center">Join as a user</h2>
              </div>
              <div class="col-12 col-sm-8 mx-auto text-left">
                <div class="card">
                  <div class="card-body">
                    <SignupBox signup-text="Sign up & join the game" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container-fluid" v-if="gameConfig.players && gameConfig.players.length > 1">
      <div class="row">
        <div class="col-12 col-lg-3 mb-3">
          <game-info :game="game" :config="gameConfig" :client-side-data="clientSideData" />
          <chat-and-notes :game="game" :client-side-data="clientSideData" />
        </div>
        <div class="col-12 col-lg-5">
          <component v-bind:is="gameConfig.game" :config="gameConfig" :client-side-data="clientSideData"></component>
        </div>
        <div class="col-12 col-lg-4">
          <game-controls :game="game" :config="gameConfig" :client-side-data="clientSideData" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import { mapState, mapGetters } from 'vuex';
import _ from 'lodash';

import appConfig from '@/config';
import GameInfo from '@/components/GameInfo.vue';
import GameControls from '@/components/GameControls.vue';
import ChatAndNotes from '@/components/ChatAndNotes.vue';
import SignupBox from '@/components/SignupBox.vue';
import tafl from '@/components/tafl.vue';
import pentago from '@/components/pentago.vue';

export default {
  name: 'game',
  components: {
    SignupBox,
    tafl,
    pentago,
    GameInfo,
    GameControls,
    ChatAndNotes,
  },
  props: ['gameSlug'],
  computed: {
    ...mapGetters(['isLoggedIn', 'getCurrentUser', 'getUserData', 'getDefaultUsername']),
    ...mapState({
      game: state => state.game.currentGame,
    }),
  },
  watch: {
    'game.winner': {
      handler() {
        if (this.game && this.game.winner && !this.calledGameOver) {
          this.gameOver();
        }
      },
      deep: true,
    },
    // 'game.moves': {
    //   handler() {
    //     this.clientSideData.moveIndex = this.game.moves.length - 1;
    //   },
    //   deep: true,
    // },
  },
  created() {
    this.resetData();
    this.onNewGame();
  },
  beforeDestroy() {
    // window.removeEventListener('keydown', this.handleKeydown);
    this.resetData();
  },
  methods: {
    setGameSlug() {
      if (!this.gameSlug) {
        console.log(this.gameConfig.game);
        this.$root.$emit('setGameSlug', this.gameConfig.game);
      }
    },
    updatePlayerRatings() {
      const updatePlayerRatings = window.firebase.functions().httpsCallable('updatePlayerRatings');

      updatePlayerRatings({ game_id: this.$route.params.game_id })
        .catch((err) => {
          console.log('err');
          console.log(err);
        });
    },
    resetData() {
      this.showSignupBox = false;
      this.gameConfig = {};
      this.clientSideData = {
        moveIndex: 0,
      };
      this.types = appConfig.types;
      this.link = window.location.href;

      if (typeof this.unsub !== 'undefined') {
        this.unsub();
      }

      this.$root.$off('move', this.onMove);
      this.$root.$off('offer-rematch', this.offerRematch);
      this.$root.$off('accept-rematch', this.answerRematch);
      this.$root.$off('decline-rematch', this.answerRematch);
    },
    onNewGame() {
      const db = window.firebase.firestore();
      const gameRef = db.collection('games').doc(this.$route.params.game_id);

      gameRef.get().then((gameDoc) => {
        this.gameConfig = gameDoc.data();
        this.gameConfig.id = this.$route.params.game_id;
        this.setGameSlug();

        if (this.canJoin()) {
          this.joinTheGame();
        }
      });

      this.unsub = gameRef.onSnapshot((doc) => {
        const gameDoc = doc.data();

        if (gameDoc.players.length === 2) {
          this.gameConfig = gameDoc;
        }

        if (typeof gameDoc.the_game !== 'undefined') {
          this.$store.dispatch('createGame', gameDoc);
        }

        if (typeof gameDoc.rematch !== 'undefined' && gameDoc.rematch.accepted) {
          this.$router.push(`/games/${gameDoc.rematch.id}`);
        }

        if (this.game && this.game.finished && typeof gameDoc.rematch === 'undefined') {
          gameRef.update({
            rematch: { offered: false },
          });
        }

        if (this.game && this.game.finished && typeof gameDoc.players[0].endRating === 'undefined') {
          this.updatePlayerRatings();
        }

        if (gameDoc.rematch && gameDoc.rematch.id) {
          this.$router.push(`/create_game/${gameDoc.rematch.id}`);
        }
      });

      // window.addEventListener('keydown', this.handleKeydown);
      // this.$root.$on('selectMove', this.onSelectMove);
      this.$root.$on('move', this.onMove);
      this.$root.$on('offer-rematch', this.offerRematch);
      this.$root.$on('accept-rematch', this.answerRematch);
      this.$root.$on('decline-rematch', this.answerRematch);
    },
    getRating() {
      const userData = this.getUserData;
      if (this.getCurrentUser && this.getCurrentUser.isAnonymous) {
        return 0;
      }

      if (userData && userData.ratings && userData.ratings[this.gameConfig.game] && userData.ratings[this.gameConfig.game][this.gameConfig.type]) {
        return userData.ratings[this.gameConfig.game][this.gameConfig.type].rating;
      }

      return 1500;
    },
    offerRematch({ by }) {
      const db = window.firebase.firestore();
      const gameRef = db.collection('games').doc(this.$route.params.game_id);

      gameRef.update({
        rematch: { offered: true, by },
      });
    },
    getMySide() {
      const p = this.gameConfig.config.playerInfo.find(pl => pl.player === this.getCurrentUser.uid);
      return p.side;
    },
    getOtherSide() {
      const p = this.gameConfig.config.playerInfo.find(pl => pl.player !== this.getCurrentUser.uid);
      return p.side;
    },
    answerRematch({ accepted }) {
      const db = window.firebase.firestore();
      const gameRef = db.collection('games').doc(this.$route.params.game_id);

      const { by, offered } = this.gameConfig.rematch;
      if (accepted) {
        const {
          min, inc, type, game, mode, config,
        } = this.gameConfig;

        this.$root.$emit('createGame', {
          min, inc, type, game, mode, config, lobby: false, side: this.getOtherSide(),
        }, (doc) => {
          gameRef.update({
            rematch: {
              accepted, by, offered, id: doc.id,
            },
          });
          this.$router.push(`/create_game/${doc.id}`);
        });
      } else {
        gameRef.update({
          rematch: {
            accepted, by, offered,
          },
        });
      }
    },
    gameOver() {
      if (this.game) {
        this.calledGameOver = true;
        const db = window.firebase.firestore();
        const gameRef = db.collection('games').doc(this.$route.params.game_id);

        gameRef.update({
          the_game: this.game.serialize(),
        });
      }
    },
    onMove({ from, to }) {
      const db = window.firebase.firestore();
      const gameRef = db.collection('games').doc(this.$route.params.game_id);

      // this.game.move(from, to);
      this.$store.dispatch('move', { from, to });

      gameRef.update({
        the_game: this.game.serialize(),
      });
    },
    // onSelectMove(index) {
    //   this.clientSideData.moveIndex = index;
    // },
    // handleKeydown(e) {
    //   const keyToMethodMap = {
    //     ArrowRight: 'next',
    //     ArrowLeft: 'prev',
    //   };

    //   if (this[keyToMethodMap[e.key]]) {
    //     this[keyToMethodMap[e.key]](e);
    //   }
    // },
    // prev() {
    //   if (this.clientSideData.moveIndex >= 0) {
    //     this.clientSideData.moveIndex -= 1;
    //   }
    // },
    // next() {
    //   if (this.clientSideData.moveIndex < this.game.moves.length - 1) {
    //     this.clientSideData.moveIndex += 1;
    //   }
    // },
    copyText(link) {
      this.$copyText(link);
      this.showCopied = true;
    },
    joinAsAUser() {
      this.showSignupBox = true;
    },
    canJoin() {
      if (this.gameConfig.mode === 'casual') {
        return true;
      }

      return !this.getCurrentUser.isAnonymous;
    },
    joinTheGame() {
      const db = window.firebase.firestore();
      const gameRef = db.collection('games').doc(this.$route.params.game_id);

      const userData = this.getUserData;

      let username = this.getDefaultUsername;
      if (userData && userData.username) {
        ({ username } = userData);
      }

      const userToJoin = {
        uid: this.getCurrentUser.uid,
        username,
        rating: this.getRating(),
      };

      // if there is only one player in the game
      if (this.gameConfig.playerCount === 1 && this.gameConfig.players[0].uid !== userToJoin.uid) {
        const sides = _.without(Object.keys(this.sideNames[this.gameConfig.game]), 'r');
        const otherSide = (this.gameConfig.config.playerInfo[0].side === sides[0]) ? sides[1] : sides[0];

        const playerInfoToJoin = {
          side: otherSide,
          player: userToJoin.uid,
          captures: 0,
        };

        const { players } = this.gameConfig;
        players.push(userToJoin);

        const { playerInfo } = this.gameConfig.config;
        playerInfo.push(playerInfoToJoin);

        gameRef.update({
          config: this.gameConfig.config,
          players,
          playerCount: 2,
        });
      }
    },
    isRoomCreator() {
      if (!this.gameConfig || !this.gameConfig.players
       || !this.gameConfig.players[0] || !this.gameConfig.players[0].username) {
        return false;
      }
      if (!this.getCurrentUser) {
        return false;
      }
      return this.gameConfig.players[0].uid === this.getCurrentUser.uid;
    },
  },
  data() {
    return {
      showCopied: false,
      sideNames: appConfig.sideNames,
      calledGameOver: false,
      unsub: undefined,
      showSignupBox: false,
      gameConfig: {},
      clientSideData: {
        moveIndex: 0,
      },
      types: appConfig.types,
      link: window.location.href,
    };
  },
};
</script>
